import type { FeatureSettings, InclusiveState } from '../types';
import { defaultSettings } from '../types';

const storageKey = (applicationId: string): string =>
    `inclusive:${applicationId}:prefs`;

const sessionKey = (applicationId: string): string =>
    `inclusive:${applicationId}:session`;

const memorySessions = new Map<string, string>();

function readStorage(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeStorage(key: string, value: string): void {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Private mode, file://, or storage disabled.
    }
}

function removeStorage(key: string): void {
    try {
        window.localStorage.removeItem(key);
    } catch {
        // Ignore.
    }
}

function createId(): string {
    if (typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);

    if (typeof crypto.getRandomValues === 'function') {
        crypto.getRandomValues(bytes);
    } else {
        for (let index = 0; index < bytes.length; index++) {
            bytes[index] = Math.floor(Math.random() * 256);
        }
    }

    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, '0'),
    ).join('');

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function getSessionId(applicationId: string): string {
    const key = sessionKey(applicationId);
    const existing = readStorage(key) ?? memorySessions.get(key);

    if (existing) {
        return existing;
    }

    const id = createId();
    memorySessions.set(key, id);
    writeStorage(key, id);

    return id;
}

export function loadPreferences(
    applicationId: string,
): Partial<InclusiveState> | null {
    try {
        const raw = readStorage(storageKey(applicationId));

        if (!raw) {
            return null;
        }

        return JSON.parse(raw) as Partial<InclusiveState>;
    } catch {
        return null;
    }
}

export function savePreferences(state: InclusiveState): void {
    writeStorage(
        storageKey(state.options.applicationId),
        JSON.stringify({
            language: state.language,
            profile: state.profile,
            settings: state.settings,
            voiceEngine: state.voiceEngine,
            browserVoiceURI: state.browserVoiceURI,
            readingRate: state.readingRate,
        }),
    );
}

export function clearPreferences(applicationId: string): void {
    removeStorage(storageKey(applicationId));
}

export function settingsFromStorage(
    stored: Partial<InclusiveState> | null,
): FeatureSettings {
    return { ...defaultSettings(), ...stored?.settings };
}
